import classNames from "classnames";
import { Fragment, useMemo, useState } from "react";
import {
    Picker,
    pickerTreeRowPlacements,
} from "@design-system/Picker/Picker.lib";
import type { PickerItemValue } from "@design-system/Picker/Picker.types";
import { PlusIcon } from "@icons";
import { ScheduleCard } from "./ScheduleCard";
import { ScheduleCardMenu } from "./ScheduleCardMenu";
import { ScheduleCardValues } from "./scheduleCardCopy";
import {
    CHANGE_SCHEDULE_ITEM,
    NO_SCHEDULE_ITEM,
    RESUME_SCHEDULE_ITEM,
    resolveSchedulePickerState,
    toggleCollapsedGroup,
} from "./schedulePickerItems";
import styles from "./SchedulePicker.module.scss";

/** The least a surface must know about a template to offer it. */
export interface SchedulePickerItem {
    id: string;
    name: string;
    group: string;
}

interface Props<TTemplate extends SchedulePickerItem> {
    templates: TTemplate[];
    /** The applied schedule, rendered as the card trigger; null shows "+ Add". */
    applied?: ScheduleCardValues | null;
    readonly?: boolean;
    isDisabled?: boolean;
    /**
     * Controlled open state. The transactions column drives this so the page
     * can reopen a cell after a template is created mid-flow
     * (registerCellActionOpener); surfaces without that hand-off omit both and
     * let the picker manage itself.
     */
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    /** Floats a deep-linked or just-created template to the top of its group. */
    prioritizedTemplateId?: string;
    /**
     * Why this template cannot be picked here, or null when it can. Items with
     * a reason render disabled with it as a tooltip. Surfaces that accept every
     * template omit this.
     */
    getBlockReason?: (template: TTemplate) => string | null;
    /**
     * Runs the surface's apply or hand-off. The picker closes on selection,
     * before any confirm or date modal opens over it; the result is the
     * surface's own, for its replace flow.
     */
    onSelect: (template: TTemplate) => void | Promise<boolean>;
    /**
     * Pins a "No schedule" row above the groups while a schedule is applied.
     * Omit until the surface can unapply (PRO-11569 owns that confirm flow).
     */
    onClear?: () => void;
    /**
     * Re-prices the schedule already applied (amount / term). Omitted on
     * surfaces that cannot host the change form, which hides the row.
     */
    onChangeSchedule?: () => void;
    /** Restarts a paused schedule. Omitted unless the applied one is paused. */
    onResumeSchedule?: () => void;
    /** Opens this schedule in the hub. Omitted until the surface can. */
    onViewSchedule?: () => void;
    /**
     * Opens schedule creation. Omitted on surfaces that cannot host it,
     * which hides the row.
     */
    onCreate?: () => void;
    /**
     * Side-panel field: text trigger ("Set recognition schedule" / name) instead
     * of the schedule card + row menu used in table columns.
     */
    compact?: boolean;
    compactEmptyLabel?: string;
    compactTriggerClassName?: string;
}

/**
 * The Schedule column's picker, shared by the transactions and invoice grids so
 * one column reads the same wherever it appears. Owns only the chrome and the
 * open/close lifecycle; each surface supplies its own templates, eligibility
 * rule and apply behaviour. Templates list under their hub group
 * (Figma 9518:5898).
 */
export function SchedulePicker<TTemplate extends SchedulePickerItem>({
    templates,
    applied,
    readonly,
    isDisabled,
    isOpen: controlledIsOpen,
    onOpenChange,
    prioritizedTemplateId,
    getBlockReason,
    onSelect,
    onClear,
    onChangeSchedule,
    onResumeSchedule,
    onViewSchedule,
    onCreate,
    compact = false,
    compactEmptyLabel = "Set recognition schedule",
    compactTriggerClassName,
}: Props<TTemplate>) {
    const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [collapsedGroupNames, setCollapsedGroupNames] = useState<
        ReadonlySet<string>
    >(new Set());
    const toggleGroup = (groupName: string) =>
        setCollapsedGroupNames((current) =>
            toggleCollapsedGroup(current, groupName),
        );
    const isOpen = controlledIsOpen ?? uncontrolledIsOpen;
    const setIsOpen = onOpenChange ?? setUncontrolledIsOpen;

    const {
        groups,
        hasNoScheduleRow,
        hasChangeScheduleRow,
        hasResumeScheduleRow,
        items,
    } = useMemo(
        () =>
            resolveSchedulePickerState({
                templates,
                search,
                prioritizedTemplateId,
                collapsedGroupNames,
                canClear: applied != null && onClear != null,
                canChange: applied != null && onChangeSchedule != null,
                canResume: applied != null && onResumeSchedule != null,
            }),
        [
            applied,
            collapsedGroupNames,
            onClear,
            onChangeSchedule,
            onResumeSchedule,
            prioritizedTemplateId,
            search,
            templates,
        ],
    );
    const templatesById = useMemo(
        () =>
            new Map<PickerItemValue, TTemplate>(
                templates.map((template) => [template.id, template]),
            ),
        [templates],
    );
    const isSearching = search.trim() !== "";

    const trigger = (() => {
        if (compact) {
            return (
                <Picker.ButtonTrigger
                    render={
                        <Picker.Button
                            variant="primary"
                            size="md"
                            className={compactTriggerClassName}
                        />
                    }
                    value={applied?.name}
                    placeholder={compactEmptyLabel}
                    showChevron={false}
                />
            );
        }
        if (applied) {
            return (
                <div className={styles.appliedRow}>
                    <Picker.Trigger
                        className={styles.cardTrigger}
                        render={(triggerProps) => (
                            <ScheduleCard
                                {...triggerProps}
                                schedule={applied}
                            />
                        )}
                    />
                    <ScheduleCardMenu
                        className={styles.cardMenu}
                        onEdit={onChangeSchedule}
                        onViewSchedule={onViewSchedule}
                    />
                </div>
            );
        }
        return <Picker.CellContent readOnly={readonly} />;
    })();

    const handleOpenChange = (nextIsOpen: boolean) => {
        if (!nextIsOpen) {
            setSearch("");
        }
        setIsOpen(nextIsOpen);
    };

    const runAction = (action: (() => void) | undefined) => {
        handleOpenChange(false);
        action?.();
    };

    return (
        <div
            className={classNames(styles.trigger, {
                [styles.triggerApplied]: applied != null,
            })}
        >
            <Picker.Select
                searchable
                value={null}
                open={isOpen}
                onOpenChange={handleOpenChange}
                onInputValueChange={setSearch}
                onValueChange={(id) => {
                    const template =
                        id === null ? undefined : templatesById.get(id);
                    if (!template || getBlockReason?.(template)) {
                        return;
                    }
                    handleOpenChange(false);
                    void onSelect(template);
                }}
                disabled={readonly || isDisabled}
            >
                {trigger}
                <Picker.Popup className={styles.schedulePicker}>
                    <Picker.Search placeholder="Search..." />
                    <Picker.List>
                        {hasResumeScheduleRow && (
                            <Picker.Item
                                onSelect={() => runAction(onResumeSchedule)}
                            >
                                {RESUME_SCHEDULE_ITEM.name}
                            </Picker.Item>
                        )}
                        {hasChangeScheduleRow && (
                            <Picker.Item
                                onSelect={() => runAction(onChangeSchedule)}
                            >
                                {CHANGE_SCHEDULE_ITEM.name}
                            </Picker.Item>
                        )}
                        {hasNoScheduleRow && (
                            <Picker.Item onSelect={() => runAction(onClear)}>
                                {NO_SCHEDULE_ITEM.name}
                            </Picker.Item>
                        )}
                        {groups.map((group) => {
                            const templatePlacements = pickerTreeRowPlacements([
                                0,
                                ...group.templates.map(() => 1),
                            ]).slice(1);
                            return (
                                <Fragment key={group.name}>
                                    <Picker.Item
                                        expandable={!isSearching}
                                        expanded={group.isExpanded}
                                        onExpandedChange={() =>
                                            toggleGroup(group.name)
                                        }
                                        onSelect={() => toggleGroup(group.name)}
                                        closeOnSelect={false}
                                    >
                                        {group.name}
                                    </Picker.Item>
                                    {group.isExpanded &&
                                        group.templates.map(
                                            (template, index) => {
                                                const blockReason =
                                                    getBlockReason?.(
                                                        template,
                                                    ) ?? null;
                                                return (
                                                    <Picker.Item
                                                        key={template.id}
                                                        value={template.id}
                                                        depth={1}
                                                        treePosition={
                                                            templatePlacements[
                                                                index
                                                            ].treePosition
                                                        }
                                                        disabled={
                                                            blockReason !== null
                                                        }
                                                        disabledReason={
                                                            blockReason
                                                        }
                                                    >
                                                        {template.name}
                                                    </Picker.Item>
                                                );
                                            },
                                        )}
                                </Fragment>
                            );
                        })}
                        {items.length === 0 && groups.length === 0 && (
                            <Picker.Empty>No policies found</Picker.Empty>
                        )}
                    </Picker.List>
                    {onCreate ? (
                        <Picker.Footer>
                            <Picker.Item
                                leading={<PlusIcon />}
                                onSelect={() => runAction(onCreate)}
                            >
                                Create new policy
                            </Picker.Item>
                        </Picker.Footer>
                    ) : null}
                </Picker.Popup>
            </Picker.Select>
        </div>
    );
}
